import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import './TeamTrackRecord.css';

const TeamTrackRecord = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [numbersRef, numbersInView] = useInView({
    triggerOnce: true,
    threshold: 0.3
  });

  // Counter animation for numbers
  const [usersCount, setUsersCount] = useState(0);
  const [institutionsCount, setInstitutionsCount] = useState(0);

  useEffect(() => {
    if (numbersInView) {
      // Animate users count
      let usersCurrent = 0;
      const usersTarget = 350;
      const usersInterval = setInterval(() => {
        usersCurrent += 10;
        if (usersCurrent >= usersTarget) {
          setUsersCount(usersTarget);
          clearInterval(usersInterval);
        } else {
          setUsersCount(usersCurrent);
        }
      }, 30);

      // Animate institutions count
      let institutionsCurrent = 0;
      const institutionsTarget = 4000;
      const institutionsInterval = setInterval(() => {
        institutionsCurrent += 100;
        if (institutionsCurrent >= institutionsTarget) {
          setInstitutionsCount(institutionsTarget);
          clearInterval(institutionsInterval);
        } else {
          setInstitutionsCount(institutionsCurrent);
        }
      }, 30);

      return () => {
        clearInterval(usersInterval);
        clearInterval(institutionsInterval);
      };
    }
  }, [numbersInView]);

  const partnerLogos = [
    { name: 'Microsoft', src: '/companyLOGO/Microsoft.png' },
    { name: 'Huawei', src: '/companyLOGO/Huawei.png' },
    { name: 'Meitu', src: '/companyLOGO/meitu.png' },
    { name: 'Amazon', src: '/companyLOGO/aws.png' }
  ];

  const industryLogos = [
    { name: 'Tencent', src: '/companyLOGO/tencent.png' },
    { name: 'Alibaba', src: '/companyLOGO/alibaba.png' },
    { name: 'Ant Group', src: '/companyLOGO/Ant_Group_logo.png' },
    { name: 'Tongcheng Lvyou', src: '/companyLOGO/tongchenglvyou.jpeg' },
    { name: 'Baidu', src: '/companyLOGO/Baidu-Logo.png' },
    { name: 'Harmonic', src: '/companyLOGO/harmonic.png' },
    { name: 'Google DeepMind', src: '/companyLOGO/googleDeepMind.webp' },
    { name: 'Standard Chartered', src: '/companyLOGO/Standard_Chartered.png' },
    { name: 'AT&T', src: '/companyLOGO/AT&T_logo.png' },
    { name: 'Wynn Resorts', src: '/companyLOGO/Wynn_Resorts_logo_2025.png' },
    { name: 'Lenovo', src: '/companyLOGO/Lenovo.png' },
    { name: 'ByteDance', src: '/companyLOGO/ByteDance_logo.png' },
    { name: 'Morgan Stanley', src: '/companyLOGO/morganStanley.png' },
    { name: 'HSBC', src: '/companyLOGO/HSBC.png' },
    { name: 'State Street', src: '/companyLOGO/stateStreet.jpeg' }
  ];

  const { language } = useLanguage();
  const t = getTranslation(language);

  return (
    <section className="team-track-record" ref={ref}>
      <div className="team-track-container">
        
        {/* Block 1 - Our Team Today */}
        <motion.div
          className="team-hero-block"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="team-hero-block-inner">
            <div className="team-hero-content">
              <h2 className="team-hero-title">{t.about.team.title}</h2>
              <p className="team-hero-text">
                <strong>{t.about.team.description}</strong>
              </p>
              <p className="team-hero-description">
                {t.about.team.detail}
              </p>
            </div>
            <div className="team-hero-visual">
              <div className="team-stat-cluster">
                <div className="team-stat-item">
                  <div className="stat-label">{t.about.team.roles.researchers}</div>
                </div>
                <div className="team-stat-item">
                  <div className="stat-label">{t.about.team.roles.engineers}</div>
                </div>
                <div className="team-stat-item">
                  <div className="stat-label">{t.about.team.roles.productOps}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Block 2 - Proven Track Record */}
        <motion.div
          className="track-record-block"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="track-record-block-inner">
            <h3 className="track-record-title">{t.about.trackRecord.title}</h3>
            
            <div className="track-record-grid">
            {/* Left Column - Achievements */}
            <div className="achievements-column">
              <ul className="achievements-list">
                {t.about.trackRecord.achievements.map((achievement, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: achievement }} />
                ))}
              </ul>
            </div>

            {/* Right Column - Metrics and Logos */}
            <div className="metrics-column" ref={numbersRef}>
              <div className="product-logos-section">
                <div className="product-logos-label">{t.about.trackRecord.products}</div>
                <div className="product-logos">
                  <div className="product-logo-item">LeapAI</div>
                  <div className="product-logo-item">KEEP</div>
                </div>
              </div>

              <div className="metrics-highlights">
                <motion.div
                  className="metric-card"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={numbersInView ? { scale: 1, opacity: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div className="metric-number">{usersCount > 0 ? `${usersCount}K+` : '350K+'}</div>
                  <div className="metric-label">{t.about.trackRecord.metrics.users}</div>
                </motion.div>

                <motion.div
                  className="metric-card"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={numbersInView ? { scale: 1, opacity: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <div className="metric-number">{institutionsCount > 0 ? `${institutionsCount.toLocaleString()}+` : '4,000+'}</div>
                  <div className="metric-label">{t.about.trackRecord.metrics.institutions}</div>
                </motion.div>
              </div>

              <div className="partners-section">
                <div className="partners-label">{t.about.trackRecord.partners}</div>
                <div className="partners-logos">
                  {partnerLogos.map((logo, index) => (
                    <div key={index} className="partner-logo-item">
                      <img src={logo.src} alt={logo.name} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </div>
        </motion.div>

        {/* Block 3 - Deep Industry Experience */}
        <motion.div
          className="industry-experience-block"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="industry-experience-block-inner">
            <h3 className="industry-experience-title">{t.about.industry.title}</h3>
            <p className="industry-experience-description">
              {t.about.industry.description}
            </p>
            
            <div className="industry-logos-grid">
              {industryLogos.map((company, index) => (
                <div key={index} className="industry-logo-item">
                  <img src={company.src} alt={company.name} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default TeamTrackRecord;
